import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import {
  AUTH_FRAME_CLOSED,
  AUTH_FRAME_AUTH_SUCCESSFUL,
  AUTH_FRAME_READY,
  AUTH_FRAME_AUTH_EVENT,
} from '@chegg-wt/core/components/AuthIframe/authLogic'
import { useSelector, useDispatch } from 'react-redux'
import { AuthSuccessGrant } from '@chegg/wtf-components'
import { AuthEventData } from '@chegg/wtf-components/dist/types/opencomponents/cheggAuth'
import {
  SuperAuthIFrameModalData,
  SuperAuthIframeOrigin,
} from '@chegg-wt/core/components/Modals/SuperAuthIFrameModal/types'
import {
  syntheticLocationChange,
  toModalCreateAccount,
} from '@chegg-wt/core/state/instrumentation/actions'
import {
  formSubmitStopped,
  formPageAuthComplete,
  writingFormSubmit,
} from '@chegg-wt/core/state/form/actions'
import { useEffect, useState } from 'react'
import { SADefaultFlow } from '@chegg-wt/core/constants/site'
import { dispatchAuthEvent } from '@chegg-wt/core/state/user/utils'
import { getCitationsFromSessionStorage } from '@chegg-wt/core/utils/writingForms'
import { EXPORT_MODAL } from '../state/modals/constants'
import { NextJsRoute } from '../types/core'
import { navigated } from '../state/app/actions'
import { openModal } from '../state/modals/actions'
import { selectedCitationsSelector } from '../state/bibliography/selectors'
import { currentStyleSelector } from '../state/currentProject/selectors'
import { modalDataSelector } from '../state/modals/selectors'
import { isAuthLoadingSelector } from '../state/user/selectors'
import usePostMessageListener from './usePostMessageListener'

interface SaIframeEventsHandlerArgs {
  closeModal: (origin?: string) => void
  login: (authSuccessGrant: AuthSuccessGrant, redirectTo?: NextJsRoute) => void
}

export const useSaIframeLoginRedirect = () => {
  const { toSourceTypeSelection, toPlagiarism } = useRoutesContext()
  const currentStyle = useSelector(currentStyleSelector)
  const data = useSelector(modalDataSelector) as SuperAuthIFrameModalData
  switch (data.origin) {
    case SuperAuthIframeOrigin.CREATE_CITATION_HOMEPAGE:
      return toSourceTypeSelection({ style: currentStyle }) as NextJsRoute
    case SuperAuthIframeOrigin.CHECK_PAPER_HOMEPAGE:
      return toPlagiarism() as NextJsRoute
    case SuperAuthIframeOrigin.SOURCE_BANNER_PAGE:
    default:
      return undefined
  }
}

const useSaIframeEventsHandler = ({ login, closeModal }: SaIframeEventsHandlerArgs) => {
  const redirectTo = useSaIframeLoginRedirect()
  const data = useSelector(modalDataSelector) as SuperAuthIFrameModalData
  const dispatch = useDispatch()
  const selectedCitations = useSelector(selectedCitationsSelector)
  const [loginStarted, setLoginstarted] = useState(false)
  const isAuthLoading = useSelector(isAuthLoadingSelector)

  const redirectOnClose = () => {
    if (
      !data.hardGate &&
      (data.origin === SuperAuthIframeOrigin.CREATE_CITATION_HOMEPAGE ||
        data.origin === SuperAuthIframeOrigin.CHECK_PAPER_HOMEPAGE)
    ) {
      dispatch(navigated(redirectTo))
    }
  }

  const onCloseModal = () => {
    switch (data.origin) {
      case SuperAuthIframeOrigin.VIEW_PAGE_EXPORT:
        dispatch(openModal({ name: EXPORT_MODAL, data: { selectedCitations } }))
        break
      case SuperAuthIframeOrigin.CITATION_FORM_PAGE: {
        const writingFormsCitationData = getCitationsFromSessionStorage()
        const { citation, initialCitationData } = writingFormsCitationData
        dispatch(writingFormSubmit({ citation, initialCitationData }))
        break
      }
      case SuperAuthIframeOrigin.REDESIGNED_UNLOCK_MODAL:
        dispatch(formPageAuthComplete({ alwaysParticipate: data.alwaysParticipate }))
        break
      default:
        break
    }
  }

  const onModalReady = () => {
    if (data.defaultFlow === SADefaultFlow.SIGN_UP) {
      dispatch(syntheticLocationChange({ routeName: toModalCreateAccount }))
    }
  }

  const onModalSuccess = (payload: AuthSuccessGrant) => {
    login(payload, redirectTo)
    setLoginstarted(true)
  }

  const onModalEvent = (payload: AuthEventData) => dispatchAuthEvent(payload, dispatch)

  useEffect(() => {
    if (loginStarted && !isAuthLoading) {
      closeModal()
      onCloseModal()
    }
  })

  const [isSAReady, setIsSAReady] = useState(false)

  /**
   * 1:- Handling Superauth frame closed event
   * 2:- Handling Superauth login/account creation success event
   */
  const postMessageSetup = usePostMessageListener({
    messageHandler: ({ type, payload }) => {
      switch (type) {
        case AUTH_FRAME_READY:
          setIsSAReady(true)
          onModalReady()
          break
        case AUTH_FRAME_CLOSED:
          if (
            data.origin === SuperAuthIframeOrigin.REDESIGNED_UNLOCK_MODAL ||
            data.origin === SuperAuthIframeOrigin.CITATION_FORM_PAGE
          ) {
            dispatch(formSubmitStopped())
          }

          closeModal(data.origin)
          redirectOnClose()
          break
        case AUTH_FRAME_AUTH_EVENT:
          onModalEvent(payload)
          break
        case AUTH_FRAME_AUTH_SUCCESSFUL:
          onModalSuccess(payload)
          break
        default:
          break
      }
    },
  })

  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dispatch(formSubmitStopped())
        closeModal(data.origin)
        redirectOnClose()
      }
    }
    if (isSAReady) {
      postMessageSetup.expectedSourceRef.current.focus()
      postMessageSetup.expectedSourceRef.current.addEventListener('keyup', onKeyUp)
    }
    return () =>
      postMessageSetup.expectedSourceRef.current.removeEventListener('keyup', onKeyUp)
  }, [postMessageSetup.expectedSourceRef.current, isSAReady])

  return { ...postMessageSetup, isSAReady }
}

export default useSaIframeEventsHandler
